/**
 * Flow Configuration Loader
 * 
 * Loads and provides access to flow configuration from flow-config.json
 * Supports multiple flows in the same codebase
 */

class FlowConfig {
  constructor() {
    this.config = null;
    this.loaded = false;
  }

  /**
   * Load configuration from flow-config.json
   * @returns {Promise<Object>} Configuration object
   */
  async load() {
    if (this.loaded && this.config) {
      return this.config;
    }

    try {
      const url = window.__wizard1FlowConfigUrl || '/config/flow-config.json';
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load flow config: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Load selling plans from centralized config
      try {
        const sellingPlansUrl = '/assets/selling-plans.json';
        const sellingPlansResponse = await fetch(sellingPlansUrl);
        if (sellingPlansResponse.ok) {
          const sellingPlansData = await sellingPlansResponse.json();
          // Merge selling plans into each flow config
          Object.keys(data.flows || {}).forEach(flowName => {
            if (data.flows[flowName]) {
              // Remove old sellingPlanMapping if it exists
              delete data.flows[flowName].sellingPlanMapping;
              // Add reference to centralized selling plans
              data.flows[flowName].sellingPlanMapping = sellingPlansData.sellingPlanMapping || {};
              if (sellingPlansData.freebieSellingPlanId) {
                data.flows[flowName].freebieSellingPlanId = String(sellingPlansData.freebieSellingPlanId);
              }
              if (Array.isArray(sellingPlansData.freebieVariantIds) && sellingPlansData.freebieVariantIds.length) {
                data.flows[flowName].freebieVariantIds = sellingPlansData.freebieVariantIds.map(String);
              }
            }
          });
        }
      } catch (error) {
        console.warn('Failed to load centralized selling plans, using fallback:', error);
        // If centralized file fails, keep existing sellingPlanMapping if it exists
      }

      // Wizard-2 (and any other flow) may ship with sellingPlanMapping: {} in flow-config.json.
      // When selling-plans.json fails to load, only wizard-1 keeps its inline map — others stay empty
      // and checkout breaks ("No selling plan found for N days"). Copy wizard-1's map as fallback.
      const flows = data.flows || {};
      const w1 = flows['wizard-1'];
      const w1Map = w1 && w1.sellingPlanMapping && typeof w1.sellingPlanMapping === 'object'
        ? w1.sellingPlanMapping
        : null;
      if (w1Map && Object.keys(w1Map).length > 0) {
        Object.keys(flows).forEach(flowName => {
          const f = flows[flowName];
          if (!f || typeof f !== 'object') return;
          const m = f.sellingPlanMapping;
          if (!m || typeof m !== 'object' || Object.keys(m).length === 0) {
            f.sellingPlanMapping = { ...w1Map };
          }
        });
      }
      
      this.config = data;
      this.loaded = true;
      
      return this.config;
    } catch (error) {
      console.error('Error loading flow configuration:', error);
      throw error;
    }
  }

  /**
   * Get configuration for a specific flow
   * @param {string} flowName - Flow name (e.g., 'wizard-1')
   * @returns {Promise<Object>} Flow configuration object
   */
  async getFlowConfig(flowName) {
    const config = await this.load();
    
    if (!config.flows || !config.flows[flowName]) {
      throw new Error(`Flow '${flowName}' not found in configuration`);
    }

    const flowConfig = config.flows[flowName];
    
    if (!flowConfig.enabled) {
      throw new Error(`Flow '${flowName}' is disabled`);
    }

    return flowConfig;
  }

  /**
   * Get default flow configuration
   * @returns {Promise<Object>} Default flow configuration
   */
  async getDefaultFlowConfig() {
    const config = await this.load();
    const defaultFlowName = config.defaultFlow || 'wizard-1';
    return this.getFlowConfig(defaultFlowName);
  }

  /**
   * Get all enabled flows
   * @returns {Promise<Array>} Array of enabled flow configurations
   */
  async getEnabledFlows() {
    const config = await this.load();
    return Object.values(config.flows || {}).filter(flow => flow.enabled);
  }
}

// Export singleton instance
const flowConfig = new FlowConfig();

// Export for global access
window.FlowConfig = FlowConfig;
window.flowConfig = flowConfig;

